import React, { useState } from 'react';
import './MarketplacePracticeLocation.scss';
import { Input } from '@availity/form';
import { FormGroup } from 'reactstrap';
import { EntityLocationInfo } from '../../../../slices/profiles-data-slice/models/entity-location-info';
import {
    LocationType,
    LocationTypeOptions,
    locationTypeTooltipHtml,
} from '../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import Helpers from '../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../Modal/ConfirmationModal';
import StateSelectField from '../../../FormFields/StateSelectField/StateSelectField';
import { getIn, useFormikContext } from 'formik';
import { useServiceFocuses } from '../../../../custom-hooks/useServiceFocuses';
import { isOtherFocus } from '../../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { NotificationFlag } from '../../../NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../../models/NotificationFlags/NotificationFlagsCategories';
import { StarRating } from '../../../StarRating/StarRating';
import InfoTooltip from '../../../info-tooltip/info-tooltip';
import LocationImagesModal from '../LocationImagesModal/LocationImagesModal';
import { PracticeProfileImage } from '../../../../slices/profiles-data-slice/models/practice-profile-image';
import { getEmptyGuid } from '../../../../utils/typedHelper';

interface MarketplacePracticeLocationProps {
    location: EntityLocationInfo;
    index: number;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    removeLocation(locationIndex: number): void;
    isRemovalAllowed?: boolean;
    isEditable?: boolean;
    canEditRatings?: boolean;
    showReadonlyRatings?: boolean;
    isOtherPracticeFocusColumnVisible?: boolean;
    timelineId?: number;
    practiceId?: string;
    profileId?: string;
    practiceUploads?: PracticeProfileImage[];
    showImagesFromColumn?: boolean;
    showImagesFromLocationId?: string;
    onShowImagesFromChange?(locationId: string): void;
}

const RATING_PLATFORMS = [
    { key: 'google', label: 'Google' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'yelp', label: 'Yelp' },
];

const MarketplacePracticeLocation = (
    props: MarketplacePracticeLocationProps
) => {
    const { isEditable = true, isRemovalAllowed = true, canEditRatings = false, showReadonlyRatings = false, timelineId, practiceId } = props;
    const serviceFocuses = useServiceFocuses();
    const location = props.location;
    const [
        isDeleteConfirmationPopupVisible,
        setIsDeleteConfirmationPopupVisible,
    ] = useState(false);
    const [isImagesModalVisible, setIsImagesModalVisible] = useState(false);

    // A location added via "+ Add Another Location" keeps the empty GUID until
    // the form is saved — the backend can't attach images to it yet.
    const isUnsavedLocation =
        !location.id || location.id === getEmptyGuid();

    const locationImages = (props.practiceUploads || []).filter(
        (upload) => upload.locationId === location.id
    );
    const { values, errors } = useFormikContext();

    const fieldName = (field: string) =>
        `${props.fieldPrefix}[${props.index}].${field}`;
    const ratingsFieldPrefix = fieldName('ratings');

    const formatRatingDisplay = (v: unknown): string => {
        if (v === '' || v == null) return '';
        const n = Number(v);
        if (isNaN(n)) return '';
        const clamped = Math.min(5, Math.max(0, n));
        return clamped % 1 === 0 ? clamped.toString() : clamped.toFixed(1);
    };

    const isOtherPracticeFocusInputVisible = () =>
        isOtherFocus(serviceFocuses, getIn(values, fieldName('practiceFocusType')));

    const handleLocationRemoval = () => {
        props.removeLocation(props.index);
    };

    const locationFieldBadge = (field: string, includePrefix?: boolean) =>
        timelineId && practiceId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                includePrefix={includePrefix}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}.PracticeLocations.${location.id}.${field}`}
            />
        ) : null;

    const locationRowBadge = () =>
        timelineId && practiceId ? (
            <NotificationFlag
                flagClassName="notification-flag-wrapper"
                doNotRemove={true}
                includePrefix={true}
                eventCode={`${NotificationFlagsCategories.MpCode}.${timelineId}.${NotificationFlagsCategories.SellerProfileUpdatedCode}.PracticeProfiles.${practiceId}.PracticeLocations.${location.id}`}
            />
        ) : null;

    const cardTitle =
        getIn(values, fieldName('dbaName')) ||
        location.dbaName ||
        location.locationName ||
        `Location ${props.index + 1}`;

    const field = (
        label: React.ReactNode,
        badgeField: string,
        control: React.ReactNode,
        className = ''
    ) => (
        <div className={`location-card__field ${className}`}>
            <label className="location-card__field-label">
                <span className="label-with-badge">
                    {label}
                    {locationFieldBadge(badgeField)}
                </span>
            </label>
            {control}
        </div>
    );

    return (
        <>
            <div className="location-card">
                <div className="location-card__header">
                    <span className="location-card__title">
                        <span className="label-with-badge">
                            {cardTitle}
                            {locationRowBadge()}
                        </span>
                    </span>
                    <div className="location-card__actions">
                        {props.showImagesFromColumn && (
                            <label className="location-card__radio-label">
                                <input
                                    type="radio"
                                    className="show-images-from-radio"
                                    name="edit-show-images-from"
                                    aria-label="Show images from this location"
                                    checked={
                                        location.id ===
                                        props.showImagesFromLocationId
                                    }
                                    onChange={() =>
                                        props.onShowImagesFromChange?.(
                                            location.id
                                        )
                                    }
                                />
                                Show images in carousel
                            </label>
                        )}
                        <span
                            title={
                                isUnsavedLocation
                                    ? 'Save your changes first to upload images to this location.'
                                    : undefined
                            }>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm location-images-button"
                                disabled={isUnsavedLocation}
                                onClick={() => setIsImagesModalVisible(true)}>
                                {isEditable
                                    ? `Upload Images${
                                          locationImages.length
                                              ? ` (${locationImages.length})`
                                              : ''
                                      }`
                                    : `Images (${locationImages.length})`}
                            </button>
                        </span>
                        <span
                            title={
                                isEditable && !isRemovalAllowed
                                    ? 'A practice must have at least one location.'
                                    : undefined
                            }>
                            <FontAwesomeIcon
                                size="lg"
                                icon={faTrash}
                                className={
                                    isEditable && isRemovalAllowed
                                        ? 'delete-icon'
                                        : 'delete-icon disabled'
                                }
                                onClick={() => {
                                    if (isEditable && isRemovalAllowed) {
                                        setIsDeleteConfirmationPopupVisible(
                                            true
                                        );
                                    }
                                }}
                            />
                        </span>
                    </div>
                </div>

                <div className="location-card__body">
                    {field(
                        'DBA Name',
                        'DbaName',
                        <FormGroup>
                            <Input
                                name={fieldName('dbaName')}
                                type="text"
                                disabled={!isEditable}
                                className="form-control"
                            />
                        </FormGroup>
                    )}
                    {field(
                        'Street Address',
                        'AddressInfo.StreetAddress',
                        <FormGroup>
                            <Input
                                name={fieldName('addressInfo.streetAddress')}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                            />
                        </FormGroup>,
                        'location-card__field--wide'
                    )}
                    {field(
                        'City',
                        'AddressInfo.City',
                        <FormGroup>
                            <Input
                                name={fieldName('addressInfo.city')}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                            />
                        </FormGroup>
                    )}
                    {field(
                        'State',
                        'AddressInfo.State',
                        <StateSelectField
                            name={fieldName('addressInfo.state')}
                            disabled={!isEditable}
                            className=""
                            label=""
                            placeholder="Select State"
                        />
                    )}
                    {field(
                        'Zip',
                        'AddressInfo.ZipCode',
                        <FormGroup>
                            <Input
                                name={fieldName('addressInfo.zipCode')}
                                type="text"
                                className="form-control"
                                disabled={!isEditable}
                                maxLength={5}
                                minLength={5}
                                pattern="[0-9]{5}"
                            />
                        </FormGroup>
                    )}
                    {field(
                        <>
                            Location Type
                            <InfoTooltip
                                idText={`location-type-tooltip-${location.id}`}
                                text=""
                                html={locationTypeTooltipHtml}
                                place="top"
                            />
                        </>,
                        'LocationType',
                        <FormGroup>
                            <Input
                                name={fieldName('locationType')}
                                type="select"
                                disabled={!isEditable}
                                className="form-control"
                                defaultValue={LocationType.Urban}>
                                {Object.entries(LocationTypeOptions).map(
                                    ([key, value]) => (
                                        <option key={key} value={key}>
                                            {value}
                                        </option>
                                    )
                                )}
                            </Input>
                        </FormGroup>
                    )}
                    {field(
                        'FTE DVMs',
                        'FteDvms',
                        <FormGroup>
                            <Input
                                name={fieldName('fteDvms')}
                                type="select"
                                className="form-control"
                                disabled={!isEditable}
                                defaultValue={0.5}
                                onChange={(event) => {
                                    props.setFormFieldValue(
                                        fieldName('fteDvms'),
                                        event.target.value
                                    );
                                }}>
                                {Helpers.getFteDvmsNumberList().map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.value}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    )}
                    {field(
                        'Service Focus',
                        'PracticeFocusType',
                        <FormGroup>
                            <Input
                                name={fieldName('practiceFocusType')}
                                type="select"
                                className="form-control"
                                disabled={!isEditable}>
                                {serviceFocuses.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    )}
                    {isOtherPracticeFocusInputVisible() &&
                        field(
                            'Specify Service Focus',
                            'OtherPracticeType',
                            <FormGroup>
                                <Input
                                    name={fieldName('otherPracticeType')}
                                    type="text"
                                    className="form-control"
                                    disabled={!isEditable}
                                />
                            </FormGroup>
                        )}
                </div>

                {canEditRatings && (
                    <div className="location-card__ratings">
                        {RATING_PLATFORMS.map((platform) => (
                            <div
                                key={platform.key}
                                className="location-card__rating-group">
                                <div className="location-card__field">
                                    <label className="location-card__field-label">
                                        <span className="label-with-badge">
                                            {platform.label} rating
                                            {locationFieldBadge(
                                                `Ratings.${platform.label}`
                                            )}
                                        </span>
                                    </label>
                                    <FormGroup>
                                        <Input
                                            name={`${ratingsFieldPrefix}.${platform.key}`}
                                            type="number"
                                            step="0.1"
                                            min={0}
                                            max={5}
                                            className="form-control"
                                            value={formatRatingDisplay(
                                                getIn(
                                                    values,
                                                    `${ratingsFieldPrefix}.${platform.key}`
                                                )
                                            )}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}`,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}`,
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </FormGroup>
                                    {getIn(
                                        errors,
                                        `${ratingsFieldPrefix}.${platform.key}`
                                    ) && (
                                        <div className="invalid-feedback d-block">
                                            {getIn(
                                                errors,
                                                `${ratingsFieldPrefix}.${platform.key}`
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="location-card__field">
                                    <label className="location-card__field-label">
                                        <span className="label-with-badge">
                                            {platform.label} # of reviews
                                            {locationFieldBadge(
                                                `Ratings.${platform.label}ReviewCount`
                                            )}
                                        </span>
                                    </label>
                                    <FormGroup>
                                        <Input
                                            name={`${ratingsFieldPrefix}.${platform.key}ReviewCount`}
                                            type="number"
                                            step="1"
                                            min={0}
                                            className="form-control"
                                            value={
                                                getIn(
                                                    values,
                                                    `${ratingsFieldPrefix}.${platform.key}ReviewCount`
                                                ) ?? ''
                                            }
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}ReviewCount`,
                                                    e.target.value
                                                )
                                            }
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                                                props.setFormFieldValue(
                                                    `${ratingsFieldPrefix}.${platform.key}ReviewCount`,
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!canEditRatings && showReadonlyRatings && (
                    <div className="location-card__ratings location-card__ratings--readonly">
                        {RATING_PLATFORMS.map((platform) => {
                            const ratingValue = getIn(
                                values,
                                `${ratingsFieldPrefix}.${platform.key}`
                            );
                            const reviewCount = getIn(
                                values,
                                `${ratingsFieldPrefix}.${platform.key}ReviewCount`
                            );
                            if (ratingValue == null) return null;
                            return (
                                <div
                                    key={platform.key}
                                    className="location-card__rating-readonly">
                                    <span className="location-card__field-label">
                                        {platform.label}
                                    </span>
                                    <span className="label-with-badge">
                                        <StarRating
                                            value={ratingValue}
                                            size="sm"
                                        />
                                        {locationFieldBadge(
                                            `Ratings.${platform.label}`,
                                            true
                                        )}
                                    </span>
                                    {reviewCount != null && reviewCount > 0 && (
                                        <span className="location-card__rating-count">
                                            ({reviewCount})
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {isImagesModalVisible && (
                <LocationImagesModal
                    isVisible={true}
                    setIsVisible={setIsImagesModalVisible}
                    locationName={location.dbaName || location.locationName}
                    locationId={location.id}
                    profileId={props.profileId}
                    practiceProfileId={props.practiceId}
                    images={locationImages}
                    isEditable={isEditable}
                />
            )}
            {isDeleteConfirmationPopupVisible && (
                <ConfirmationModal
                    isVisible={true}
                    setIsVisible={setIsDeleteConfirmationPopupVisible}
                    header={'Location Removal'}
                    confirmationText={`Are you sure you want to remove ${
                        location.locationName
                    }?${
                        locationImages.length
                            ? ` Its ${locationImages.length} image${
                                  locationImages.length > 1 ? 's' : ''
                              } will be removed as well.`
                            : ''
                    }`}
                    onConfirm={handleLocationRemoval}
                    nextButtonText={'Remove'}
                    cancelButtonText={'Cancel'}
                    params={null}
                />
            )}
        </>
    );
};
export default MarketplacePracticeLocation;
